/**
 * The conversation Connor is in the middle of, kept across a reload.
 *
 * Josh, 2026-09-07: "I clicked on the link and it opened the form but then when
 * I went back to the hub, connor had reset itself so I wasn't able to continue
 * the conversation." He had just been handed a PDF to look at — following it is
 * the point of the answer, and losing the thread for doing so makes the answer
 * cost something.
 *
 * ⚠️ IN THE BROWSER, THE SAME TRADE recentSearches MAKES. It works the moment it
 * ships, needs no migration, and a half-finished conversation is inherently
 * personal. The cost is real and worth stating: it does not follow somebody to
 * another machine, and clearing site data loses it.
 *
 * ⚠️ KEYED BY EMAIL, BECAUSE A CONVERSATION CAN NAME AN INSURED. On a shared
 * machine the next person to sign in must not inherit the last one's thread, so
 * a stored session belonging to another address is discarded rather than shown.
 *
 * ⚠️ AND IT EXPIRES. Resuming where you left off two minutes ago is the
 * feature; resurrecting Tuesday's half-question on Thursday is a bug that looks
 * like the app talking to itself.
 */
import type { AppetiteAnswer } from '@commpass/shared';

const KEY = 'aas.connorSession.v1';
const TTL_MS = 12 * 60 * 60 * 1000;
const MAX_MSGS = 40;

export interface StoredMsg {
  from: 'you' | 'aas';
  text?: string;
  answer?: AppetiteAnswer;
}

export interface ConnorSession {
  at: number;
  email: string;
  msgs: StoredMsg[];
  /** The model-facing history, so a resumed turn still has its context. */
  convo: { role: 'user' | 'assistant'; content: string }[];
  step: string;
  threadId: string | null;
}

/**
 * Steps that are safe to come back to.
 *
 * ⚠️ NEVER 'thinking' OR 'routing'. Those describe a request that was in flight
 * when the page went away; restoring one would show a spinner waiting on a
 * promise that no longer exists. 'human' IS restored, because relay mode with a
 * live thread is exactly the conversation worth not losing.
 */
const RESUMABLE = new Set(['intent', 'ask', 'answered', 'done', 'human', 'offered-form']);

export function loadConnorSession(email: string | null | undefined): ConnorSession | null {
  if (!email) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as ConnorSession;
    if (!s?.msgs?.length || !Array.isArray(s.msgs)) return null;
    if (s.email !== email) return null;
    if (!s.at || Date.now() - s.at > TTL_MS) return null;
    return { ...s, step: RESUMABLE.has(s.step) ? s.step : 'intent' };
  } catch {
    // A corrupt entry must not take the chat down with it.
    return null;
  }
}

export function saveConnorSession(s: Omit<ConnorSession, 'at'>): void {
  try {
    if (!s.email || !s.msgs.length) return;
    const trimmed: ConnorSession = {
      ...s,
      at: Date.now(),
      // Newest wins: an old transcript is not worth a quota error that loses
      // the current one.
      msgs: s.msgs.slice(-MAX_MSGS),
      convo: s.convo.slice(-20),
    };
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    // Storage full or refused. Losing the resume is not worth an error.
  }
}

export function clearConnorSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}
